import React, { useState, useEffect } from 'react';
import { Typography, Box, Input } from '@mui/material';
import { Search } from '@mui/icons-material';
import { useAuthHeader } from '../Services/Auth/sessionAuth';
import { Tree, Spin } from 'antd';
import type { DataNode } from 'antd/es/tree';
import axios from 'axios';
import { Crypter } from '../Functions/Security/Crypter';
import Data from '../Data/Data.json';
import CusumMenuForSelect from '../Models/CusumMenuForSelect';

interface ModuleMappingBoxProps {
  selectedModules?: string[];
  onModulesChange?: (modules: string[]) => void;
  disabled?: boolean;
  isConsultMode?: boolean;
  title?: string;
}

interface ModuleNode {
  key: string;
  title: string;
  children?: ModuleNode[];
  disabled?: boolean;
}

const ModuleMappingBox: React.FC<ModuleMappingBoxProps> = ({
  selectedModules = [],
  onModulesChange,
  disabled = false,
  isConsultMode = false,
  title = "Modules du groupe"
}) => {
  const authHeader = useAuthHeader();

  const [modules, setModules] = useState<ModuleNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadModules();
  }, []);

  const loadModules = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = Crypter.encrypt('all');
      const config = {
        method: 'get',
        maxBodyLength: Infinity,
        url: Data.ApiUrl.userMenus,
        headers: {
          'Authorization': authHeader(),
          'Content-Type': 'application/json',
          'CipherUserGroup': data
        }
      };

      const response = await axios.request(config);
      if (response.data) {
        const res = JSON.parse(Crypter.decrypt(response.data)).Value;
        if (res.StatutCode === 200) {
          const menuData = CusumMenuForSelect.getMenu(res.Result);
          setModules(menuData);
        } else {
          setError('Erreur lors du chargement des modules');
        }
      }
    } catch (err) {
      console.error('Error loading modules:', err);
      setError('Erreur de connexion au serveur');
    } finally {
      setLoading(false);
    }
  };

  const filterModules = (nodes: ModuleNode[], term: string): ModuleNode[] => {
    if (!term) return nodes;
    return nodes.reduce((acc: ModuleNode[], node) => {
      const matchesSearch = node.title.toLowerCase().includes(term.toLowerCase());
      const filteredChildren = node.children ? filterModules(node.children, term) : [];
      if (matchesSearch || filteredChildren.length > 0) {
        acc.push({
          ...node,
          children: filteredChildren.length > 0 ? filteredChildren : node.children
        });
      }
      return acc;
    }, []);
  };

  const buildTreeData = (nodes: ModuleNode[]): DataNode[] => {
    return nodes.map(n => ({
      key: n.key,
      disabled: n.disabled || false,
      title: (
        <span>
          <b>{n.title}</b>
          {selectedModules.includes(n.key) ? <span style={{ color: 'green', marginLeft: 8 }}>[Sélectionné]</span> : null}
        </span>
      ),
      children: n.children ? buildTreeData(n.children) : undefined
    }));
  };

  const filteredModules = filterModules(modules, searchTerm);
  const treeData = buildTreeData(filteredModules);

  const selectedCount = selectedModules.length;
  const totalCount = modules.length;

  return (
    <Box className='bordure1' style={{ height: 'calc(100vh - 156px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box style={{ padding: 16, borderBottom: '1px solid #e0e0e0' }}>
        <Typography style={{ fontWeight: 600, marginBottom: 8 }}>
          {title}
        </Typography>
        <Box display='flex' alignItems='center' gap={1}>
          <Input
            placeholder='Rechercher un module...'
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            disabled={disabled || isConsultMode}
            startAdornment={<Search sx={{ mr: 1, color: 'inherit' }} />}
            style={{ flexGrow: 1 }}
          />
        </Box>
        <Typography variant='caption' display='block' style={{ marginTop: 8 }}>
          {selectedCount} / {totalCount} module(s) sélectionné(s)
        </Typography>
      </Box>

      <Box style={{ flexGrow: 1, overflow: 'auto', padding: 16 }}>
        <Spin spinning={loading}>
          {error && (
            <Typography color='error' style={{ textAlign: 'center', padding: 16 }}>
              {error}
            </Typography>
          )}
          {!loading && !error && (
            <Tree
              checkable
              showLine
              defaultExpandAll
              disabled={disabled || isConsultMode}
              checkedKeys={selectedModules}
              onCheck={(checked) => onModulesChange?.(checked as string[])}
              treeData={treeData}
            />
          )}
        </Spin>
      </Box>

      {isConsultMode && (
        <Box style={{ padding: 12, backgroundColor: '#fff3cd', borderTop: '1px solid #ffecb5' }}>
          <Typography variant='caption' style={{ color: '#664d03' }}>
            Mode consultation - Les modules ne peuvent pas être modifiés
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ModuleMappingBox;
